import { useTranslation } from 'react-i18next';
import type { BLOG_POSTS } from '@/mocks/blog';
import type { BORGHI } from '@/mocks/borghi';

type PostType = typeof BLOG_POSTS[0];
type BorgoType = typeof BORGHI[0];

interface Props {
  post: PostType;
  lang: 'it' | 'en' | 'de';
  relatedBorghiData: BorgoType[];
}

const GenericArticleBody = ({ post, lang, relatedBorghiData }: Props) => {
  const { t } = useTranslation();

  const getBorgoTagline = (b: BorgoType) => {
    if (lang === 'en') return b.taglineEn;
    if (lang === 'de') return b.taglineDe;
    return b.tagline;
  };

  return (
    <div className="space-y-6">
      <p className="font-sans text-lg text-anthracite-500 leading-relaxed mb-8 border-l-4 border-terracotta-300 pl-6">
        {lang === 'en'
          ? `The Marche region represents one of Italy's best-kept secrets. Between the Adriatic coast and the Apennine mountains, this extraordinary territory offers a quality of life that few places in the world can match. The historic borghi, the rolling hills covered in vineyards and olive groves, the authentic cuisine — everything here speaks of a deep, genuine beauty.`
          : lang === 'de'
          ? `Die Region Marken ist eines der bestgehüteten Geheimnisse Italiens. Zwischen der Adriaküste und den Apenninen bietet dieses außergewöhnliche Gebiet eine Lebensqualität, die nur wenige Orte der Welt erreichen können. Die historischen Dörfer, die mit Weinbergen und Olivenhainen bedeckten Hügel, die authentische Küche — alles hier spricht von einer tiefen, echten Schönheit.`
          : `La regione Marche rappresenta uno dei segreti meglio custoditi d'Italia. Tra la costa adriatica e i monti Appennini, questo territorio straordinario offre una qualità della vita che pochi luoghi al mondo possono eguagliare. I borghi storici, le colline coperte di vigneti e uliveti, la cucina autentica — tutto qui parla di una bellezza profonda e genuina.`
        }
      </p>

      {/* Inline image */}
      <div className="rounded-xl overflow-hidden aspect-[16/9] mb-8">
        <img
          src={`https://readdy.ai/api/search-image?query=$%7Bpost.slug.replace%28%2F-%2Fg%2C%20%20%29%7D%20Marche%20Italy%20beautiful%20landscape%20borghi%20lifestyle%20editorial%20photography%20warm%20light&width=900&height=500&seq=blogInline${post.slug}&orientation=landscape`}
          alt={post.title}
          className="w-full h-full object-cover object-top"
        />
      </div>

      <div className="prose prose-lg max-w-none space-y-6">
        <h4 id="sezione-1" className="font-serif text-2xl text-anthracite-700 font-light">
          <a href="#sezione-1" className="hover:text-terracotta-300 transition-colors">
            {lang === 'en' ? 'Why choose Marche' : lang === 'de' ? 'Warum die Marken wählen' : 'Perché scegliere le Marche'}
          </a>
        </h4>
        <p className="font-sans text-base text-anthracite-500 leading-relaxed">
          {lang === 'en'
            ? `The Marche region offers an extraordinary combination of natural beauty, cultural heritage and quality of life. Property prices remain significantly lower than Tuscany or Umbria, while the quality of life is comparable or superior. The region is easily accessible from major European airports and offers excellent infrastructure.`
            : lang === 'de'
            ? `Die Region Marken bietet eine außergewöhnliche Kombination aus natürlicher Schönheit, kulturellem Erbe und Lebensqualität. Die Immobilienpreise sind deutlich niedriger als in der Toskana oder Umbrien, während die Lebensqualität vergleichbar oder überlegen ist.`
            : `Le Marche offrono una combinazione straordinaria di bellezza naturale, patrimonio culturale e qualità della vita. I prezzi degli immobili rimangono significativamente inferiori rispetto alla Toscana o all'Umbria, mentre la qualità della vita è comparabile o superiore. La regione è facilmente raggiungibile dai principali aeroporti europei.`
          }
        </p>

        <h4 id="sezione-2" className="font-serif text-2xl text-anthracite-700 font-light">
          <a href="#sezione-2" className="hover:text-terracotta-300 transition-colors">
            {lang === 'en' ? 'The borghi: living history' : lang === 'de' ? 'Die Dörfer: lebendige Geschichte' : 'I borghi: storia vivente'}
          </a>
        </h4>
        <p className="font-sans text-base text-anthracite-500 leading-relaxed">
          {lang === 'en'
            ? `Each borgo in Marche has its own unique identity, its own story to tell. From Castelfidardo, world capital of the accordion, to Corinaldo with its perfectly preserved medieval walls, to Mondavio dominated by the magnificent Rocca Roveresca — every village is a world unto itself, waiting to be discovered.`
            : lang === 'de'
            ? `Jedes Dorf in den Marken hat seine eigene einzigartige Identität, seine eigene Geschichte zu erzählen. Von Castelfidardo, der Welthauptstadt des Akkordeons, bis Corinaldo mit seinen perfekt erhaltenen mittelalterlichen Mauern — jedes Dorf ist eine Welt für sich.`
            : `Ogni borgo delle Marche ha la propria identità unica, la propria storia da raccontare. Da Castelfidardo, capitale mondiale della fisarmonica, a Corinaldo con le sue mura medievali perfettamente conservate, a Mondavio dominata dalla magnifica Rocca Roveresca — ogni paese è un mondo a sé, in attesa di essere scoperto.`
          }
        </p>
      </div>

      {/* Related borghi */}
      {relatedBorghiData.length > 0 && (
        <div className="mt-12 pt-10 border-t border-ivory-200">
          <p className="font-sans text-xs tracking-widest uppercase text-anthracite-400 mb-6">{t('blog.borghi.correlati')}</p>
          <div className="flex flex-wrap gap-4">
            {relatedBorghiData.map((b) => (
              <a
                key={b.slug}
                href={`/borghi/${b.slug}`}
                className="group flex items-center gap-3 bg-ivory-50 border border-ivory-200 rounded-xl px-4 py-3 hover:border-terracotta-300 transition-all cursor-pointer"
              >
                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={b.image} alt={b.name} className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <p className="font-serif text-sm text-anthracite-700 group-hover:text-terracotta-300 transition-colors">{b.name}</p>
                  <p className="font-sans text-xs text-anthracite-400">{getBorgoTagline(b).substring(0, 40)}...</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GenericArticleBody;
